import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FaStar, FaCheck, FaSpinner, FaShoppingCart } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import Swal from 'sweetalert2'; 
import SummaryApi from "../common";

const ProductCards = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedIds, setExpandedIds] = useState([]);

  // State to track which items are currently being added (loading spinner)
  const [processingIds, setProcessingIds] = useState([]);
  
  // State to track which items are successfully added (check mark)
  const [addedIds, setAddedIds] = useState([]);

  // Get User ID
  const customerId = sessionStorage.getItem("customer_id");

  // 1. Fetch Products List
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const res = await fetch(SummaryApi.product.productRetrieveApi.url, {
          method: SummaryApi.product.productRetrieveApi.method,
          headers: {
            Authorization: SummaryApi.bearerToken,
          },
        });

        const data = await res.json();
        if (data.status && Array.isArray(data.data)) {
          setProducts(data.data);
        } else {
          setProducts([]);
        }
      } catch (error) {
        console.error("Fetch Error:", error);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // 2. Fetch User's Cart Items (To sync "Added" state from server)
  useEffect(() => {
    const fetchCartStatus = async () => {
      if (!customerId) return;

      try {
        const response = await fetch(SummaryApi.getCartItems.url, {
          method: SummaryApi.getCartItems.method,
          headers: {
            'Content-Type': 'application/json',
            'Authorization': SummaryApi.bearerToken
          },
          body: JSON.stringify({ customer_id: customerId })
        });

        const data = await response.json();

        if (data.status && Array.isArray(data.data)) {
          // Extract just the product IDs that are in the cart
          // We convert to string to ensure type safety during comparison
          const cartProductIds = data.data.map(item => item.product_id.toString());
          setAddedIds(cartProductIds);
        }
      } catch (error) {
        console.error("Error checking cart status:", error);
      }
    };

    fetchCartStatus();
  }, [customerId]);

  // Toast Configuration
  const Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 2000,
    timerProgressBar: true,
    didOpen: (toast) => {
      toast.addEventListener('mouseenter', Swal.stopTimer)
      toast.addEventListener('mouseleave', Swal.resumeTimer)
    }
  });

 const handleAddToCart = async (e, product) => {
    e.stopPropagation(); 

    const prodId = product.product_id.toString();

    // 1. Check if user is logged in
    if (!customerId) {
        Swal.fire({
            icon: 'warning',
            title: 'Please Login',
            text: 'You need to login to add items to the cart.',
            confirmButtonText: 'Login Now'
        }).then((result) => {
            if (result.isConfirmed) navigate('/login');
        });
        return;
    }

    // 2. If already added, navigate to the Cart Page
    if (addedIds.includes(prodId)) {
        navigate('/cart'); 
        return;
    }

    // 3. Add to Cart Logic
    try {
      setProcessingIds(prev => [...prev, prodId]);

      const response = await fetch(SummaryApi.addToCart.url, {
        method: SummaryApi.addToCart.method,
        headers: {
            'Content-Type': 'application/json',
            'Authorization': SummaryApi.bearerToken
        },
        body: JSON.stringify({
            customer_id: customerId,
            product_id: prodId,
            quantity: 1
        })
      });

      const data = await response.json();

      if (data.status) {
        // Update local state so button changes immediately
        setAddedIds(prev => [...prev, prodId]);

        // ---------------------------------------------------------
        // ✅ ADD THIS LINE HERE:
        // This tells the Header component to re-fetch the cart count immediately
        // ---------------------------------------------------------
        window.dispatchEvent(new Event("cart-updated")); 
        
        Toast.fire({
            icon: 'success',
            title: `${product.product_name} added to cart`
        });
      } else {
        Toast.fire({
            icon: 'error',
            title: data.message || 'Failed to add'
        });
      }
    } catch (error) {
      console.error("Add to cart error:", error);
      Toast.fire({
        icon: 'error',
        title: 'Something went wrong'
      });
    } finally {
      setProcessingIds(prev => prev.filter(id => id !== prodId));
    }
  };

  const handleViewDetails = (product) => {
 navigate(`/product-details`, { state: { product } });
  };

  const toggleReadMore = (id) => {
    setExpandedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  // Animations
  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.05 } },
  };

  const cardItem = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
  };

  const ProductSkeleton = () => (
    <div className="bg-white rounded-3xl p-4 shadow-sm animate-pulse border border-gray-100">
      <div className="bg-gray-200 h-48 rounded-2xl w-full mb-4"></div>
      <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
      <div className="h-3 bg-gray-200 rounded w-full mb-2"></div>
      <div className="h-6 bg-gray-200 rounded w-1/3 mt-4"></div>
    </div>
  );

  return (
    <section className="bg-[#f8fbff] py-12 sm:py-16 lg:py-20">
      <motion.div
        className="text-center mb-10 sm:mb-12 px-4"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        <h2 className="text-[#0a1f44] text-2xl sm:text-3xl lg:text-4xl font-bold mb-3">
          Premium ingredients for a richer oral care experience
        </h2>
        <p className="text-[#7d8da1] text-sm sm:text-base max-w-2xl mx-auto">
          Each flavor is carefully crafted to deliver the perfect balance of taste and dental care.
        </p>
      </motion.div>

      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-[8%]">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {[1, 2, 3, 4].map((n) => <ProductSkeleton key={n} />)}
          </div>
        ) : (
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {products.length === 0 ? (
              <p className="text-center col-span-4 text-gray-500">No products found.</p>
            ) : (
              products.map((product) => {
                const imageUrl = `${SummaryApi.productImage.url}${product.product_img1}`;
                
                const isExpanded = expandedIds.includes(product.product_id);
                const truncatedDesc = product.product_description.slice(0, 60);
                
                // Convert IDs to string for safe comparison
                const prodIdStr = product.product_id.toString();
                const isProcessing = processingIds.includes(prodIdStr);
                const isAdded = addedIds.includes(prodIdStr);

                return (
                  <motion.div
                    key={product.product_id}
                    variants={cardItem}
                    whileHover={{ y: -5 }}
                    onClick={() => handleViewDetails(product)}
                    className="bg-white w-full rounded-3xl shadow-[0_8px_30px_rgba(0,0,0,0.08)] hover:shadow-[0_20px_50px_rgba(0,0,0,0.15)] transition-all duration-300 overflow-hidden cursor-pointer flex flex-col h-full"
                  >
                    <div className="overflow-hidden h-48 sm:h-56 md:h-60 relative bg-gray-50">
                      <img
                        src={imageUrl}
                        alt={product.product_name}
                        loading="lazy"
                        className="w-full h-full  transition-transform duration-500 hover:scale-105"
                      />
                    </div>

                    <div className="p-5 sm:p-6 flex flex-col justify-between flex-grow">
                      <div>
                        <h3 className="text-[#0a1f44] font-semibold text-base sm:text-lg mb-1">
                          {product.product_name}
                        </h3>

                        <p className="text-[#7d8da1] text-sm mb-3">
                          {isExpanded
                            ? product.product_description
                            : `${truncatedDesc}${product.product_description.length > 60 ? "..." : ""}`}
                          {product.product_description.length > 60 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleReadMore(product.product_id);
                              }}
                              className="text-blue-600 hover:text-blue-800 ml-1 font-medium"
                            >
                              {isExpanded ? " Show less" : " Read more"}
                            </button>
                          )}
                        </p>

                        <div className="flex items-center mb-2">
                          {[...Array(5)].map((_, i) => (
                            <FaStar
                              key={i}
                              className={`text-xs sm:text-sm ${
                                i < Number(product.rating || 0)
                                  ? "text-[#ffb400]"
                                  : "text-gray-300"
                              }`}
                            />
                          ))}
                          <span className="ml-2 text-[#7d8da1] text-xs sm:text-sm">
                            ({product.rating || 0})
                          </span>
                        </div>

                        <p className="text-[#0a1f44] font-bold text-base sm:text-lg mb-4">
                          ₹ {product.after_discount_price}
                        </p>
                      </div>

                      {/* Dynamic Add to Cart Button */}
                      <button
                        onClick={(e) => handleAddToCart(e, product)}
                        disabled={isProcessing}
                        className={`w-full py-2 rounded-full font-medium text-sm sm:text-base transition-all duration-200 flex items-center justify-center gap-2
                          ${isAdded 
                            ? "bg-green-600 hover:bg-green-700 text-white" // Style for Added state
                            : "bg-[#0a3c6e] hover:bg-[#0b4a8d] text-white" // Style for Default state
                          }
                          ${isProcessing ? "opacity-75 cursor-not-allowed" : "active:scale-95"}
                        `}
                      >
                        {isProcessing ? (
                          <>
                            <FaSpinner className="animate-spin" /> Adding...
                          </>
                        ) : isAdded ? (
                          <>
                            <FaCheck /> Added to Cart
                          </>
                        ) : (
                          "Add to Cart"
                        )}
                      </button>
                    </div>
                  </motion.div>
                );
              })
            )}
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default ProductCards;